import { Game, GameType } from "@aura-frontend/omnibus-az-client";
import { PlayerPublic } from "@aura-frontend/omnibus-az-client";
import { toast } from "@win-reality/react-ui";
import { Dispatch } from "redux";

import {
  clearBattingPracticeRoster,
  setBattingPracticeRoster,
  setBattingPracticeTeamName,
} from "../../../store/slices/battingPractice";
import {
  endBullpen,
  resetBullpenPitchCount,
  setBullpenActive,
  setBullpenPitcher,
} from "../../../store/slices/bullpen";
import {
  endGame,
  setAreRostersOpen,
  setCurrentLineup,
  setGameActive,
  setRoster,
  setTeam,
} from "../../../store/slices/game";
import { setSessionType, setSessionUuid } from "../../../store/slices/session";
import {
  resetEventQueue,
  setTrackingState,
} from "../../../store/slices/socket";
import { setActiveView } from "../../../store/slices/view";
import {
  getHomeAwayLineups,
  mapLineupEntryToIPLayer,
  mapPublicPlayerToIPlayer,
} from "../../../utils/api-helpers";

export const endGameEffects = (dispatch: Dispatch) => {
  dispatch(endGame());
  dispatch(endBullpen());
  dispatch(clearBattingPracticeRoster());
  dispatch(setSessionType(""));
  dispatch(setSessionUuid(""));
  dispatch(setTrackingState(false));
  dispatch(setAreRostersOpen(true));
  dispatch(resetEventQueue());
};

/**
 * Resumes a game of type "game". It rehydrates data into the store.
 */
export const resumeGame = (
  dispatch: Dispatch,
  { game, awayTeamPlayers, homeTeamPlayers }: ResumeGameEffectsPayload,
) => {
  dispatch(setSessionUuid(game.gameUuid));
  dispatch(setGameActive(true));
  dispatch(setAreRostersOpen(false));

  // @ts-expect-error displayName is not in the type
  const [homeTeam, awayTeam] = game.displayName.split(" x ");

  dispatch(
    setTeam({
      team: "away",
      name: awayTeam,
      uuid: game.teams.away,
    }),
  );
  dispatch(
    setTeam({
      team: "home",
      name: homeTeam,
      uuid: game.teams.home,
    }),
  );

  dispatch(
    setRoster({
      team: "away",
      roster: awayTeamPlayers.map(mapPublicPlayerToIPlayer),
    }),
  );

  dispatch(
    setRoster({
      team: "home",
      roster: homeTeamPlayers.map(mapPublicPlayerToIPlayer),
    }),
  );

  const { home: homeLineup, away: awayLineup } = getHomeAwayLineups(game);

  dispatch(
    setCurrentLineup({
      team: "away",
      lineup: awayLineup.map(mapLineupEntryToIPLayer),
    }),
  );

  dispatch(
    setCurrentLineup({
      team: "home",
      lineup: homeLineup.map(mapLineupEntryToIPLayer),
    }),
  );

  dispatch(setSessionType("game"));
  dispatch(setTrackingState(true));
  dispatch(setActiveView("Game Mode"));
};

/**
 * Resumes a game of type "bullpen". It rehydrates data into the store.
 */
const resumeBullpen = (
  dispatch: Dispatch,
  payload: ResumeGameEffectsPayload,
) => {
  const bullpenPitcherEntry = getHomeAwayLineups(payload.game).home.find(
    (entry) => entry.position === "pitcher",
  );

  dispatch(setSessionUuid(payload.game.gameUuid));
  dispatch(setActiveView("Bullpen Mode"));

  if (!bullpenPitcherEntry) {
    dispatch(setBullpenActive(true));
    return;
  }

  const bullpenPitcher = mapLineupEntryToIPLayer(bullpenPitcherEntry);
  dispatch(setBullpenPitcher(bullpenPitcher));
  dispatch(resetBullpenPitchCount());
  dispatch(setBullpenActive(true));
  toast(
    `${bullpenPitcher.firstName} ${bullpenPitcher.lastName} is the bullpen pitcher!`,
    { variant: "success" },
  );
};

/**
 * Resumes a game of type "batting_practice". It rehydrates data into the store.
 */
const resumeBattingPractice = (
  dispatch: Dispatch,
  payload: ResumeGameEffectsPayload,
) => {
  dispatch(setSessionUuid(payload.game.gameUuid));

  // @ts-expect-error displayName is not in the type
  const [homeTeamName] = payload.game.displayName.split(" x ");

  dispatch(setBattingPracticeTeamName(homeTeamName));
  dispatch(
    setBattingPracticeRoster(
      getHomeAwayLineups(payload.game).home.map(mapLineupEntryToIPLayer),
    ),
  );
  dispatch(setActiveView("Batting Practice Mode"));
};

const resumeGameByType: Record<
  GameType,
  (dispatch: Dispatch, payload: ResumeGameEffectsPayload) => void
> = {
  game: resumeGame,
  bullpen: resumeBullpen,
  batting_practice: resumeBattingPractice,
};

type ResumeGameEffectsPayload = {
  game: Game;
  awayTeamPlayers: PlayerPublic[];
  homeTeamPlayers: PlayerPublic[];
};

export const resumeGameEffects = (
  dispatch: Dispatch,
  payload: ResumeGameEffectsPayload,
) => {
  const gameType = payload.game.type as GameType;
  resumeGameByType[gameType](dispatch, payload);
};
